import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { PushQueuePanel, PushRulesPanel } from '../PushNotices/PushNoticeSettings';

const bannerUnitPattern = /^ca-app-pub-\d+\/\d+$/;

function AppFeatureRow({ label, checked, disabled, onChange, busy }) {
  return (
    <div className="flex align-items-center gap-3 mb-4">
      <InputSwitch
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span>{label}</span>
      {busy ? <Button loading text /> : null}
    </div>
  );
}

function AdUnitField({ id, platform, icon, iconColor, hint, value, disabled, onChange }) {
  const valid = bannerUnitPattern.test(String(value || '').trim());
  return (
    <div className="col-12 md:col-6">
      <div className="flex flex-column gap-2">
        <label className="font-semibold flex align-items-center gap-2" htmlFor={id}>
          <span>Ad unit ID del banner - {platform}</span>
          <i className={icon} aria-hidden="true" style={{ color: iconColor }} />
        </label>
        <p className="admin-page-hint m-0">{hint}</p>
        <div style={{ position: 'relative' }}>
          <InputText
            id={id}
            className="w-full"
            value={value}
            disabled={disabled}
            style={{ paddingRight: 40 }}
            onChange={onChange}
          />
          {valid ? (
            <i
              className="pi pi-check-circle"
              aria-label="Ad unit válido"
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#16a34a',
                fontSize: 18,
                pointerEvents: 'none',
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

const adDefaults = {
  bannerAdUnitId: '',
  iosBannerAdUnitId: '',
  timesEnabled: true,
  chatEnabled: true,
  chatEveryNMessages: 8,
  chatTallEveryNthAd: 4,
  chatListEnabled: true,
  newsEnabled: true,
  newsEveryNItems: 4,
  newsTallEveryNthAd: 3,
  newsDetailEnabled: true,
};

const etaDefaults = {
  updateIntervalMinutes: 2,
  minDeltaMinutes: 3,
  cycleWindowMinutes: 20,
  minElapsedMinutes: 2,
  minMovedMeters: 40,
  stallMeters: 15,
  stallMultiplier: 2,
  stallFloorMinutes: 8,
  sampleEverySeconds: 30,
};

function staleMinutesOf(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return 3;
  return Math.min(180, Math.max(1, n));
}

export default function AppSettingsManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [avatarsEnabled, setAvatarsEnabled] = useState(false);
  const [inLineEnabled, setInLineEnabled] = useState(true);
  const [myCrossingsEnabled, setMyCrossingsEnabled] = useState(true);
  const [proUpgradeBannerEnabled, setProUpgradeBannerEnabled] = useState(true);
  const [liveTrackStaleMinutes, setLiveTrackStaleMinutes] = useState(3);
  const [directionSwitchEnabled, setDirectionSwitchEnabled] = useState(true);
  const [directionSwitchMessage, setDirectionSwitchMessage] = useState('');
  const [eta, setEta] = useState(etaDefaults);
  const [ads, setAds] = useState(adDefaults);

  const applyStories = (data) => {
    setEnabled(Boolean(data?.enabled));
    setAvatarsEnabled(data?.avatarsEnabled === true);
    setInLineEnabled(data?.inLineEnabled !== false);
    setMyCrossingsEnabled(data?.myCrossingsEnabled !== false);
    setProUpgradeBannerEnabled(data?.proUpgradeBannerEnabled !== false);
    setLiveTrackStaleMinutes(staleMinutesOf(data?.liveTrackStaleMinutes));
    setDirectionSwitchEnabled(data?.directionSwitchEnabled !== false);
    setDirectionSwitchMessage(data?.directionSwitchMessage || '');
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/stories_settings');
        applyStories(data);
        const etaRes = await api.get('/eta_settings');
        setEta({ ...etaDefaults, ...(etaRes.data || {}) });
        const adRes = await api.get('/ad_settings');
        setAds({ ...adDefaults, ...(adRes.data || {}) });
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar la configuración',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (patch, optimistic) => {
    const previous = {
      enabled,
      avatarsEnabled,
      inLineEnabled,
      myCrossingsEnabled,
      proUpgradeBannerEnabled,
      liveTrackStaleMinutes,
      directionSwitchEnabled,
      directionSwitchMessage,
    };
    if (optimistic) applyStories({ ...previous, ...optimistic });
    try {
      setSaving(true);
      const { data } = await api.put('/stories_settings', patch);
      applyStories(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Listo',
        detail: 'Configuración guardada',
      });
    } catch (err) {
      applyStories(previous);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo guardar',
      });
    } finally {
      setSaving(false);
    }
  };

  const saveAds = async () => {
    try {
      setSaving(true);
      const { data } = await api.put('/ad_settings', ads);
      setAds({ ...adDefaults, ...(data || {}) });
      toast.current?.show({
        severity: 'success',
        summary: 'Listo',
        detail: 'Anuncios guardados. Las apps los toman dentro de 1 hora.',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron guardar los anuncios',
      });
    } finally {
      setSaving(false);
    }
  };

  const saveEta = async () => {
    try {
      setSaving(true);
      const { data } = await api.put('/eta_settings', eta);
      setEta({ ...etaDefaults, ...(data || {}) });
      toast.current?.show({
        severity: 'success',
        summary: 'Listo',
        detail: 'ETA local guardado. Las apps lo toman dentro de 24 horas.',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo guardar el ETA',
      });
    } finally {
      setSaving(false);
    }
  };

  const etaField = (key, label, hint) => (
    <div className="col-12 md:col-4">
      <div className="flex flex-column gap-2 h-full">
        <label className="font-semibold" htmlFor={`eta-${key}`}>{label}</label>
        <p className="admin-page-hint m-0">{hint}</p>
        <InputNumber
          inputId={`eta-${key}`}
          className="w-full"
          inputClassName="w-full"
          style={{ width: '100%' }}
          value={eta[key]}
          disabled={loading || saving}
          minFractionDigits={key === 'stallMultiplier' ? 1 : 0}
          maxFractionDigits={key === 'stallMultiplier' ? 1 : 0}
          onValueChange={(e) => setEta((prev) => ({ ...prev, [key]: e.value ?? etaDefaults[key] }))}
        />
      </div>
    </div>
  );

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <h2 className="m-0 mb-3 main-text font-bold">Settings</h2>
        <TabView>
          <TabPanel header="App">
        <p className="admin-page-hint mb-4">
          Controles de funciones de la app. Los cambios llegan al abrir o al refrescar la app
          (pull to refresh en Tiempos o Perfil). Las historias suben fotos a Cloudinary y se borran a las 24 horas.
        </p>
        <AppFeatureRow
          label="Historias en el chat"
          checked={enabled}
          disabled={loading || saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save({ enabled: next }, { enabled: next });
          }}
        />
        <AppFeatureRow
          label="Avatars"
          checked={avatarsEnabled}
          disabled={loading || saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save({ avatarsEnabled: next }, { avatarsEnabled: next });
          }}
        />
        <AppFeatureRow
          label="Estoy en la fila"
          checked={inLineEnabled}
          disabled={loading || saving}
          busy={saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save({ inLineEnabled: next }, { inLineEnabled: next });
          }}
        />
        <AppFeatureRow
          label="Card Mis cruces en perfil"
          checked={myCrossingsEnabled}
          disabled={loading || saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save({ myCrossingsEnabled: next }, { myCrossingsEnabled: next });
          }}
        />
        <AppFeatureRow
          label="Banner Subir a Pro"
          checked={proUpgradeBannerEnabled}
          disabled={loading || saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save(
              { proUpgradeBannerEnabled: next },
              { proUpgradeBannerEnabled: next },
            );
          }}
        />
        <div className="flex flex-column gap-2 mt-4" style={{ maxWidth: 420 }}>
          <label className="font-semibold" htmlFor="live-stale-minutes">
            Quitar del mapa en vivo si deja de reportar (minutos)
          </label>
          <p className="admin-page-hint m-0">
            Si alguien deja de enviar su posición, su carro desaparece del mapa después de estos minutos. El cruce sigue pudiendo sincronizarse después.
          </p>
          <div className="flex align-items-center gap-2">
            <InputNumber
              inputId="live-stale-minutes"
              value={liveTrackStaleMinutes}
              disabled={loading || saving}
              min={1}
              max={180}
              onValueChange={(e) => setLiveTrackStaleMinutes(e.value ?? 3)}
              suffix=" min"
            />
            <Button
              type="button"
              label="Guardar"
              disabled={loading || saving}
              loading={saving}
              onClick={() => save({ liveTrackStaleMinutes })}
            />
          </div>
        </div>
        <AppFeatureRow
          label="Cambio de dirección (ciudad → USA y USA → ciudad)"
          checked={directionSwitchEnabled}
          disabled={loading || saving}
          onChange={(e) => {
            const next = Boolean(e.value);
            save(
              {
                directionSwitchEnabled: next,
                directionSwitchMessage,
              },
              { directionSwitchEnabled: next },
            );
          }}
        />
        <div className="flex flex-column gap-2" style={{ maxWidth: 520 }}>
          <label className="font-semibold" htmlFor="direction-switch-message">
            Mensaje al intentar cambiar de dirección
          </label>
          <InputTextarea
            id="direction-switch-message"
            value={directionSwitchMessage}
            disabled={loading || saving}
            onChange={(e) => setDirectionSwitchMessage(e.target.value)}
            rows={3}
            autoResize
            maxLength={400}
            placeholder="Si lo dejas vacío, la app usa el mensaje por defecto"
          />
          <div>
            <Button
              type="button"
              label="Guardar mensaje"
              disabled={loading || saving}
              loading={saving}
              onClick={() => save({ directionSwitchMessage })}
            />
          </div>
        </div>
          </TabPanel>
          <TabPanel header="ETA local">
            <p className="admin-page-hint mb-4">
              Factores del ETA personal de quien está en la fila. La app los consulta una vez al día.
            </p>
            <div className="grid">
              {etaField('updateIntervalMinutes', 'Cada cuánto se actualiza el número (min)', 'La pantalla no cambia en cada movimiento.')}
              {etaField('minDeltaMinutes', 'Cambio mínimo para mostrar otro número (min)', 'Si la diferencia es menor, se queda el anterior.')}
              {etaField('cycleWindowMinutes', 'Ventana del patrón de la fila (min)', 'Incluye avances y pausas del mismo ciclo.')}
              {etaField('minElapsedMinutes', 'Minutos antes del primer ETA', 'Antes de esto solo dice que está midiendo.')}
              {etaField('minMovedMeters', 'Metros mínimos para empezar', 'Hace falta este avance real sobre el camino.')}
              {etaField('stallMeters', 'Metros que cuentan como avance', 'Por debajo de esto, ese tramo se ve como pausa.')}
              {etaField('stallMultiplier', 'Pausa larga respecto al patrón', 'Si la pausa actual supera el patrón por este factor, la fila va lenta.')}
              {etaField('stallFloorMinutes', 'Pausa mínima para decir que va lenta (min)', 'También aplica cuando todavía no hay un patrón de pausas.')}
              {etaField('sampleEverySeconds', 'Muestra del camino (s)', 'Cada cuántos segundos se guarda metros restantes para revisar el algoritmo.')}
            </div>
            <Button
              type="button"
              label="Guardar ETA"
              className="mt-2"
              disabled={loading || saving}
              loading={saving}
              onClick={saveEta}
            />
          </TabPanel>
          <TabPanel header="AdMob settings">
            <p className="admin-page-hint mb-3">
              Each platform has its own ad unit, the one with a bar. In test builds, the app still uses Google test ads.
            </p>
            <div className="grid mb-4">
              <AdUnitField
                id="ad-unit-android"
                platform="Android"
                icon="pi pi-android"
                iconColor="#3DDC84"
                hint="El de la app Android en AdMob."
                value={ads.bannerAdUnitId}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, bannerAdUnitId: e.target.value }))}
              />
              <AdUnitField
                id="ad-unit-ios"
                platform="iOS"
                icon="pi pi-apple"
                iconColor="#007AFF"
                hint="El de la app iOS en AdMob. Vacío hasta que crees esa app."
                value={ads.iosBannerAdUnitId}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, iosBannerAdUnitId: e.target.value }))}
              />
            </div>
            <div className="flex align-items-center gap-3 mb-3">
              <InputSwitch
                checked={ads.timesEnabled}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, timesEnabled: Boolean(e.value) }))}
              />
              <span>{ads.timesEnabled ? 'Banner de Tiempos prendido' : 'Banner de Tiempos apagado'}</span>
            </div>
            <div className="flex align-items-center gap-3 mb-3">
              <InputSwitch
                checked={ads.chatEnabled}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, chatEnabled: Boolean(e.value) }))}
              />
              <span>{ads.chatEnabled ? 'Anuncios dentro del chat prendidos' : 'Anuncios dentro del chat apagados'}</span>
            </div>
            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2">
                  <label className="font-semibold" htmlFor="chat-every">Mensajes reales entre anuncios</label>
                  <p className="admin-page-hint m-0">Mínimo 6. Por defecto, 8.</p>
                  <InputNumber
                    inputId="chat-every"
                    className="w-full"
                    inputClassName="w-full"
                    value={ads.chatEveryNMessages}
                    disabled={loading || saving}
                    min={6}
                    max={40}
                    onValueChange={(e) => setAds((prev) => ({ ...prev, chatEveryNMessages: e.value ?? 8 }))}
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2">
                  <label className="font-semibold" htmlFor="chat-tall">Cada cuántos anuncios uno alto</label>
                  <p className="admin-page-hint m-0">0 los deja todos en 320×50. Por defecto, el 4.º es de 300×250.</p>
                  <InputNumber
                    inputId="chat-tall"
                    className="w-full"
                    inputClassName="w-full"
                    value={ads.chatTallEveryNthAd}
                    disabled={loading || saving}
                    min={0}
                    max={12}
                    onValueChange={(e) => setAds((prev) => ({ ...prev, chatTallEveryNthAd: e.value ?? 4 }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex align-items-center gap-3 mb-3 mt-3">
              <InputSwitch
                checked={ads.chatListEnabled}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, chatListEnabled: Boolean(e.value) }))}
              />
              <span>{ads.chatListEnabled ? 'Banner en el listado de chats prendido' : 'Banner en el listado de chats apagado'}</span>
            </div>
            <div className="flex align-items-center gap-3 mb-3">
              <InputSwitch
                checked={ads.newsEnabled}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, newsEnabled: Boolean(e.value) }))}
              />
              <span>{ads.newsEnabled ? 'Anuncios en el feed de noticias prendidos' : 'Anuncios en el feed de noticias apagados'}</span>
            </div>
            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2">
                  <label className="font-semibold" htmlFor="news-every">Notas reales entre anuncios</label>
                  <p className="admin-page-hint m-0">Las tarjetas patrocinadas no cuentan. Mínimo 3. Por defecto, 4.</p>
                  <InputNumber
                    inputId="news-every"
                    className="w-full"
                    inputClassName="w-full"
                    value={ads.newsEveryNItems}
                    disabled={loading || saving}
                    min={3}
                    max={20}
                    onValueChange={(e) => setAds((prev) => ({ ...prev, newsEveryNItems: e.value ?? 4 }))}
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="flex flex-column gap-2">
                  <label className="font-semibold" htmlFor="news-tall">Cada cuántos anuncios uno alto</label>
                  <p className="admin-page-hint m-0">0 los deja todos en 320×50. Por defecto, el 3.º es de 300×250.</p>
                  <InputNumber
                    inputId="news-tall"
                    className="w-full"
                    inputClassName="w-full"
                    value={ads.newsTallEveryNthAd}
                    disabled={loading || saving}
                    min={0}
                    max={12}
                    onValueChange={(e) => setAds((prev) => ({ ...prev, newsTallEveryNthAd: e.value ?? 3 }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex align-items-center gap-3 mb-4 mt-3">
              <InputSwitch
                checked={ads.newsDetailEnabled}
                disabled={loading || saving}
                onChange={(e) => setAds((prev) => ({ ...prev, newsDetailEnabled: Boolean(e.value) }))}
              />
              <span>{ads.newsDetailEnabled ? 'Banner al final de la nota prendido' : 'Banner al final de la nota apagado'}</span>
            </div>
            <Button
              type="button"
              label="Guardar anuncios"
              disabled={loading || saving}
              loading={saving}
              onClick={saveAds}
            />
          </TabPanel>
          <TabPanel header="Push notifications">
            <PushRulesPanel toast={toast} />
          </TabPanel>
          <TabPanel header="Push notifications queue">
            <PushQueuePanel toast={toast} />
          </TabPanel>
        </TabView>
      </div>
    </Card>
  );
}
